import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate, Link } from "react-router-dom";
import { Phone, Menu, X, Mail } from "lucide-react";
import { BUSINESS } from "@/config";

const links = [
  { label: "Home", to: "/", testId: "nav-home-link", end: true },
  { label: "Services", to: "/services", testId: "nav-services-link" },
  { label: "Our Work", to: "/our-work", testId: "nav-work-link" },
  { label: "About", to: "/about", testId: "nav-about-link" },
  { label: "Contact", to: "/contact", testId: "nav-contact-link" },
];

const navLinkBase =
  "inline-flex items-center whitespace-nowrap px-3 py-2 rounded-md text-sm uppercase tracking-[0.15em] border transition-all duration-200";
const navLinkInactive =
  "text-[#A0ABC0] border-[#2C374D]/70 bg-[#0B132B]/40 hover:text-white hover:border-[#D47A4A]/50 hover:bg-[#151D33]";
const navLinkActive =
  "text-white border-[#D47A4A]/60 bg-[#151D33] shadow-[inset_0_-2px_0_0_#D47A4A]";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const handleEnquireScroll = (e) => {
    e.preventDefault();
    setOpen(false);
    if (pathname === "/") {
      const el = document.getElementById("contact");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    } else {
      navigate("/#contact");
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#0B132B]/90 border-b border-[#2C374D]"
          : "bg-[#0B132B]/50 backdrop-blur-md border-b border-transparent"
      }`}
      data-testid="site-navbar"
    >
      <nav className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-8 h-20">
        <Link
          to="/"
          className="flex items-center gap-3 group"
          data-testid="nav-logo"
        >
          <img
            src={BUSINESS.logoUrl}
            alt="Conan Sharman Plumbing logo"
            className="h-12 w-12 object-contain bg-white rounded-md p-1"
          />
          <div className="hidden sm:flex flex-col text-left leading-tight">
            <span className="font-display text-base xl:text-lg text-white tracking-wide whitespace-nowrap">
              CONAN SHARMAN
            </span>
            <span className="text-[9px] xl:text-[10px] uppercase tracking-[0.22em] text-[#D47A4A] whitespace-nowrap">
              Plumbing &amp; Drain Cleaning Ltd
            </span>
          </div>
        </Link>

        <div className="hidden lg:flex items-center gap-1.5">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={l.testId}
              className={({ isActive }) =>
                `${navLinkBase} ${isActive ? navLinkActive : navLinkInactive}`
              }
            >
              {l.label}
            </NavLink>
          ))}

          <a
            href={BUSINESS.phoneHref}
            data-testid="nav-call-button"
            className="ml-2 inline-flex items-center whitespace-nowrap gap-2 border border-[#2C374D] text-[#A0ABC0] hover:text-white hover:border-[#D47A4A]/60 px-3.5 py-2.5 rounded-md transition-colors uppercase tracking-wider text-sm"
          >
            <Phone className="w-4 h-4" />
            Call Now
          </a>
          <button
            type="button"
            onClick={handleEnquireScroll}
            data-testid="nav-enquire-button"
            className="inline-flex items-center whitespace-nowrap gap-2 bg-[#D47A4A] hover:bg-[#B85D2A] text-[#0B132B] font-bold px-4 py-2.5 rounded-md transition-all uppercase tracking-wider text-sm shadow-md shadow-[#D47A4A]/20 hover:shadow-[#D47A4A]/40 hover:-translate-y-0.5"
          >
            <Mail className="w-4 h-4" />
            Enquire Now
          </button>
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="lg:hidden text-white p-2 rounded-md"
          aria-label="Toggle menu"
          data-testid="mobile-menu-toggle"
        >
          {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </nav>

      {open && (
        <div
          className="lg:hidden bg-[#0B132B] border-t border-[#2C374D] px-4 py-6 flex flex-col gap-3"
          data-testid="mobile-menu"
        >
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={({ isActive }) =>
                `block px-4 py-3 rounded-md text-base uppercase tracking-[0.18em] border transition-colors ${
                  isActive
                    ? "text-white bg-[#151D33] border-[#D47A4A]/40"
                    : "text-[#A0ABC0] border-[#2C374D] hover:text-white hover:bg-[#151D33]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-2">
            <a
              href={BUSINESS.phoneHref}
              className="inline-flex items-center justify-center gap-2 border border-[#2C374D] text-[#A0ABC0] px-5 py-3 rounded-md uppercase tracking-wider text-sm"
            >
              <Phone className="w-4 h-4" />
              Call {BUSINESS.phone}
            </a>
            <button
              type="button"
              onClick={handleEnquireScroll}
              className="inline-flex items-center justify-center gap-2 bg-[#D47A4A] text-[#0B132B] font-bold px-5 py-3 rounded-md uppercase tracking-wider text-sm"
            >
              <Mail className="w-4 h-4" />
              Enquire Now
            </button>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
